<template>
  <div>
    <b-sidebar
      id="structure-resources-sidebar-right"
      :class="[
        'structure-sidebar-resource-modal',
        (isModalResourceInfoOpen || isModalLinkResourceOpen || isModalOptionsResourceOpen) &&
        device != 'mobile'
          ? '--role-openned'
          : '--role-closed'
      ]"
      right
      bg-variant="white"
      width="407px"
      v-model="isOpen"
      @hidden="closeModal"
    >
      <template #header>
        <div class="header-sidebar d-flex justify-content-between">
          <span class="sidebar-title">
            {{ $t(modalTitle) }}
          </span>

          <span
            class="close-icon"
            @click="closeModal()"
          >
            <CloseIcon />
          </span>
        </div>

        <div class="separator"></div>
      </template>
      <template>
        <div class="h-100">
          <div :class="device === 'mobile' ? 'd-flex justify-content-between' : ''">
            <div class="select-all">
              <div
                :class="['checkbox-container', allResourceChecked ? 'checked' : '']"
                @click="checkAllResource()"
              >
                <span class="icon-checkbox"><CheckIcon /></span>
              </div>
              <span class="text">{{ $t('WorkcenterList.Resources') }}</span>
            </div>
            <div class="action-block">
              <div
                v-if="device !== 'mobile'"
                class="link-resource"
                @click="openLinkModal()"
              >
                <LinkIcon class="link-icon" />
                <span>{{ $t('StructurePage.LinkResource') }}</span>
              </div>
              <div class="function-filter">
                <span>{{ $t('StructurePage.Type') }}</span>
                <div
                  id="filter-select-resource"
                  class="filter"
                  tabindex="0"
                >
                  <span>{{ $t(selectedFilter) }}</span>
                  <ArrowDown class="arrow-down" />
                </div>
              </div>
              <Popover
                :items="typeList"
                target="filter-select-resource"
                placement="bottom"
                :custom-position="`action-button ${
                  isModalResourceInfoOpen ? '--double-modal-openned' : ''
                }`"
                triggers="focus"
              />
            </div>
          </div>
          <div
            v-if="forceRenderResource"
            :class="['resource-list', filteredList.length >= 9 ? 'enable-scroll' : '']"
          >
            <div
              v-if="filteredList.length === 0"
              class="block-no-results"
            >
              <div class="no-results">
                <SearchIcon />
                <p class="text">{{ $t('NoResultsFound') }}</p>
              </div>
            </div>
            <div v-else>
              <div
                v-for="resource in filteredList"
                :key="resource.id"
                :class="[
                  'resource-row',
                  resourceInfo.id === resource.id && isModalResourceInfoOpen ? '--is-viewing' : '',
                  device === 'mobile' ? 'is-mobile' : ''
                ]"
                @click="openInfoResource(resource, $event)"
              >
                <div class="user-info-block">
                  <div class="check-resource">
                    <div
                      :class="[
                        'checkbox-container __no-action-default',
                        resource.checked ? 'checked' : ''
                      ]"
                      @click="toggleCheckbox(resource)"
                    >
                      <span class="icon-checkbox __no-action-default"><CheckIcon /></span>
                    </div>
                  </div>
                  <div class="text-block">
                    <div class="resource-name">
                      {{ resource.name }}
                    </div>
                    <div class="resource-code">{{ resource.code }}</div>
                  </div>
                </div>
                <div class="d-flex">
                  <div
                    v-if="device !== 'mobile' && isModalResourceInfoOpen"
                    class="view-block"
                  >
                    <EyeIcon />
                    <span>{{ $t('DepartmentPage.Viewing') }}</span>
                  </div>
                  <div
                    v-if="resource.type"
                    class="roles-block"
                  >
                    <div class="role-tag">
                      {{ $t(resource.type) }}
                    </div>
                  </div>
                  <div
                    class="unlink __no-action-default"
                    @click="unLinkResource(resource)"
                  >
                    <UnLinkIcon />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </b-sidebar>
    <modal-resource-info
      :resourceInfo="resourceInfo"
      :device="device"
      :modalTitle="modalTitle"
      @onUnlink="(resource) => unLinkResource(resource)"
      @onTransfer="(resource) => transferResource(resource)"
    />
    <modal-link-resource
      v-if="device !== 'mobile'"
      :device="device"
      :currentId="currentId"
      :prefix="prefix"
      @updateDragList="updateDragList"
    />
    <modal-options-resource
      v-if="device !== 'mobile'"
      :device="device"
      :isToUnlink="isToUnlink"
      :isToTransfer="isToTransfer"
      :currentId="currentId"
      :prefix="prefix"
      @onUnlinkResource="onUnlinkResource"
      @onTransferResource="(id) => onTransferResource(id)"
      @close="unCheckAllResource"
    />
  </div>
</template>

<script>
  import { BSidebar } from 'bootstrap-vue';
  import { mapState, mapMutations } from 'vuex';
  import Popover from '@core/layouts/layout-horizontal/components/Menu/Popover.vue';
  import ModalResourceInfo from './ModalResourceInfo.vue';
  import ModalLinkResource from './ModalLinkResource.vue';
  import ModalOptionsResource from './ModalOptionsResource.vue';
  import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

  export default {
    components: {
      BSidebar,
      ModalResourceInfo,
      ModalLinkResource,
      Popover,
      ModalOptionsResource,
      CloseIcon: () => import('@core/assets/icons/close-icon.svg'),
      CheckIcon: () => import('@core/assets/icons/check-icon.svg'),
      ArrowDown: () => import('@core/assets/icons/arrow-down-orange.svg'),
      LinkIcon: () => import('@core/assets/icons/link-icon.svg'),
      EyeIcon: () => import('@core/assets/icons/eye-icon.svg'),
      UnLinkIcon: () => import('@core/assets/icons/unlink-icon.svg'),
      SearchIcon: () => import('@core/assets/icons/search-icon.svg')
    },
    props: {
      modalTitle: {
        type: String,
        default: ''
      },
      currentId: {
        type: Number,
        default: null
      }
    },
    data() {
      return {
        isOpen: false,
        forceRenderResource: true,
        allResourceChecked: false,
        isToUnlink: false,
        isToTransfer: false,
        selectedFilter: 'DepartmentPage.All',
        prefix: localStorage.getItem('prefix'),
        filteredList: [],
        typeList: [
          {
            title: 'DepartmentPage.All',
            click: () => {
              this.selectedFilter = 'DepartmentPage.All';
            }
          },
          {
            title: 'StructurePage.Machine',
            click: () => {
              this.selectedFilter = 'StructurePage.Machine';
            }
          }
        ],
        resourceInfo: {},
        device:
          window.innerWidth > 768
            ? 'desktop'
            : window.innerWidth > 480 && window.innerWidth <= 768
            ? 'tablet'
            : 'mobile'
      };
    },
    created() {
      window.addEventListener('resize', this.handleResize);
    },
    destroyed() {
      window.removeEventListener('resize', this.handleResize);
    },
    mounted() {
      this.UPDATE_MODAL_RESOURCE_TOGGLE(false);
    },
    methods: {
      ...mapMutations('app', ['UPDATE_FLAG_SHOW_OVERLAY']),
      ...mapMutations('structures', [
        'UPDATE_MODAL_RESOURCE_TOGGLE',
        'UPDATE_MODAL_RESOURCE_INFO_TOGGLE',
        'UPDATE_MODAL_LINK_RESOURCE_TOGGLE',
        'UPDATE_RESOURCE_LIST',
        'UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE'
      ]),
      handleResize() {
        this.device =
          window.innerWidth > 768
            ? 'desktop'
            : window.innerWidth > 480 && window.innerWidth <= 768
            ? 'tablet'
            : 'mobile';
      },
      getAllTypeFilter() {
        const getAllTypes = this.filteredList.flatMap((item) => item.type);
        const uniqueTypes = new Set(getAllTypes.flat());

        this.typeList = [
          {
            title: 'DepartmentPage.All',
            click: () => {
              this.selectedFilter = 'DepartmentPage.All';
            }
          },
          ...Array.from(uniqueTypes).map((type) => ({
            title: type,
            click: () => {
              this.selectedFilter = type;
            }
          }))
        ];
      },
      updateFilterByFunction(selectedType) {
        this.filteredList =
          selectedType === 'DepartmentPage.All'
            ? this.resourceList
            : this.resourceList.filter((resource) =>
                Array.isArray(resource.type)
                  ? resource.type.includes(selectedType)
                  : resource.type === selectedType
              );
      },
      updateDragList() {
        this.$emit('updateDragList');
      },
      unLinkResource(item) {
        this.isToUnlink = true;
        this.isToTransfer = false;
        this.toggleCheckbox(item);
      },
      transferResource(item) {
        this.isToUnlink = false;
        this.isToTransfer = true;
        this.toggleCheckbox(item);
      },
      openLinkModal() {
        this.unCheckAllResource();
        this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(false);
        this.UPDATE_MODAL_RESOURCE_INFO_TOGGLE(false);
        this.UPDATE_MODAL_LINK_RESOURCE_TOGGLE(true);
      },
      unCheckAllResource() {
        if (this.resourceList.length) {
          this.resourceList.map((e) => {
            e['checked'] = false;
          });
        }
        this.allResourceChecked = false;
        this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(false);
      },
      closeModal() {
        this.UPDATE_MODAL_RESOURCE_TOGGLE(false);
        this.UPDATE_MODAL_RESOURCE_INFO_TOGGLE(false);
        this.UPDATE_MODAL_LINK_RESOURCE_TOGGLE(false);
        this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(false);
      },
      forceRenderResourceList() {
        this.forceRenderResource = false;
        this.$nextTick().then(() => {
          this.forceRenderResource = true;
        });
      },
      toggleCheckbox(elem) {
        if (!elem.checked) {
          elem['checked'] = true;
          this.UPDATE_MODAL_RESOURCE_INFO_TOGGLE(false);
          this.UPDATE_MODAL_LINK_RESOURCE_TOGGLE(false);
          this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(true);
        } else {
          elem['checked'] = false;
          this.allResourceChecked = false;
          const hasChecked = this.resourceList.some((item) => !!item.checked);
          hasChecked
            ? this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(true)
            : this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(false);
        }
        this.verifyAllChecked();
        this.forceRenderResourceList();
      },
      showToast(title, icon, text, variant) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: title,
            icon: icon,
            text: text,
            variant
          }
        });
      },
      checkAllResource() {
        this.filteredList.map((e) => {
          if (this.allResourceChecked) {
            e['checked'] = false;
            this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(false);
          } else {
            e['checked'] = true;
            this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(true);
          }
        });
        this.verifyAllChecked();
        this.UPDATE_MODAL_RESOURCE_INFO_TOGGLE(false);
        this.UPDATE_MODAL_LINK_RESOURCE_TOGGLE(false);
        this.forceRenderResourceList();
      },
      async getResource(id) {
        this.UPDATE_FLAG_SHOW_OVERLAY(true);
        await this.$http
          .get(`/api/${this.prefix}/myconfig/workcenter/${id}/resource`)
          .then((response) => {
            this.UPDATE_RESOURCE_LIST(response.data.data);
            this.updateFilterByFunction(this.selectedFilter);
            this.getAllTypeFilter();
            this.forceRenderResourceList();
            this.UPDATE_FLAG_SHOW_OVERLAY(false);
          })
          .catch((error) => console.error(error));
      },
      openInfoResource(item, elem) {
        this.resourceInfo = item;
        const isNoActionDefault = elem.target.closest('.__no-action-default');
        const hasChecked = this.resourceList.some((item) => item.checked);

        if (isNoActionDefault) return;

        if (hasChecked && !item.checked) {
          item.checked = true;
          this.UPDATE_MODAL_RESOURCE_INFO_TOGGLE(false);
        } else if (item.checked) {
          item.checked = false;
          this.allResourceChecked = false;
          !this.resourceList.some((item) => item.checked) &&
            this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(false);
        } else {
          this.UPDATE_MODAL_RESOURCE_INFO_TOGGLE(true);
          this.UPDATE_MODAL_LINK_RESOURCE_TOGGLE(false);
        }
        this.verifyAllChecked();
        this.forceRenderResourceList();
      },
      verifyAllChecked() {
        const hasUnChecked = this.filteredList.find((e) => !e.checked);
        this.allResourceChecked = hasUnChecked ? false : true;
      },
      async onUnlinkResource() {
        this.UPDATE_FLAG_SHOW_OVERLAY(true);
        const checkedResources = this.resourceList.filter((e) => !!e.checked);
        const payload = {
          resources: checkedResources.map((e) => e.id)
        };
        await this.$http
          .delete(`/api/${this.prefix}/myconfig/workcenter/${this.currentId}/resource/remove`, {
            data: payload
          })
          .then((response) => {
            this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(false);
            this.getResource(this.currentId);
            this.$emit('updateDragList');
            this.showToast(
              this.$t('IntegrationPage.success'),
              'SmileIcon',
              this.$t('StructurePage.UnlinkedResources'),
              'success'
            );
          })
          .catch((error) => console.error(error))
          .finally(() => this.UPDATE_FLAG_SHOW_OVERLAY(false));
      },
      async onTransferResource(id) {
        this.UPDATE_FLAG_SHOW_OVERLAY(true);
        const checkedResources = this.resourceList.filter((e) => !!e.checked);
        const payload = {
          workcenters_destiny_ids: [id],
          resources_ids: checkedResources.map((e) => e.id)
        };
        await this.$http
          .put(
            `/api/${this.prefix}/myconfig/workcenter/${this.currentId}/resource/transfer`,
            payload
          )
          .then((response) => {
            this.UPDATE_MODAL_OPTIONS_RESOURCE_TOGGLE(false);
            this.getResource(this.currentId);
            this.$emit('updateDragList');
            this.showToast(
              this.$t('IntegrationPage.success'),
              'SmileIcon',
              this.$t('StructurePage.TransferredResources'),
              'success'
            );
          })
          .catch((error) => {
            console.error(error);
            if (error.response?.data?.errors) {
              error.response.data.errors.map((e) => {
                const text = typeof e === 'string' ? e : (e.message || e);
                this.showToast(this.$t('Error'), 'FrownIcon', text, 'danger');
              });
            } else if (error.response?.data?.message) {
              this.showToast(this.$t('Error'), 'FrownIcon', error.response.data.message, 'danger');
            }
          })
          .finally(() => this.UPDATE_FLAG_SHOW_OVERLAY(false));
      }
    },
    computed: {
      ...mapState('structures', {
        isModalResourceOpen: 'isModalResourceOpen',
        isModalResourceInfoOpen: 'isModalResourceInfoOpen',
        isModalLinkResourceOpen: 'isModalLinkResourceOpen',
        isModalOptionsResourceOpen: 'isModalOptionsResourceOpen',
        resourceList: 'resourceList',
        rolesList: 'rolesList'
      })
    },
    watch: {
      isModalResourceOpen(v) {
        this.isOpen = v;
        if (v) {
          this.selectedFilter = 'DepartmentPage.All';
          this.getResource(this.currentId);
        } else {
          this.unCheckAllResource();
        }
      },
      isModalOptionsResourceOpen(v) {
        if (!v) {
          this.isToUnlink = false;
          this.isToTransfer = false;
        }
      },
      device(v) {
        if (v === 'mobile') {
          this.UPDATE_MODAL_RESOURCE_INFO_TOGGLE(false);
          this.unCheckAllResource();
        }
      },
      selectedFilter(v) {
        this.unCheckAllResource();
        this.updateFilterByFunction(v);
      },
      resourceList() {
        this.getAllTypeFilter();
      }
    }
  };
</script>
<style lang="scss">
  .structure-sidebar-resource-modal {
    &.--role-openned {
      .b-sidebar.b-sidebar-right {
        right: 407px;
        transition: all 0.5s ease-out;
        @media (max-width: 814px) {
          left: 0;
          width: 50vw !important;
        }
        @media (max-width: 480px) {
          left: 0;
          width: 100vw !important;
        }
      }
    }
    &.--role-closed {
      .b-sidebar.b-sidebar-right {
        right: 0;
        transition: all 0.5s ease-out;

        @media (max-width: 480px) {
          left: 0;
          width: 100vw !important;
        }
      }
    }

    .b-sidebar-header {
      display: contents;

      .header-sidebar {
        padding: 16px;

        .sidebar-title {
          display: flex;
          align-items: center;
          color: #4c4541;
          font-size: 18px;
          font-style: normal;
          font-weight: 600;

          @media (max-width: 480px) {
            font-size: 16px;
          }
        }
      }

      .close-icon {
        border: 1px solid #974900;
        border-radius: 5px;
        width: 28px;
        height: 28px;
        display: flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;

        svg {
          width: 12px;
          height: 12px;
          path {
            fill: #974900;
          }
        }

        &:hover {
          background-color: #ffede2 !important;
        }

        &:active {
          background-color: #ffdbc4 !important;
        }
      }

      .separator {
        width: 100%;
        height: 1px;
        background: #cfc4be;
      }
    }

    .b-sidebar {
      height: 100% !important;
      .b-sidebar-body {
        padding: 16px;

        .select-all {
          display: flex;
          gap: 8px;

          .text {
            color: #4c4541;
            font-size: 16px;
            font-weight: 600;
            line-height: 26px;

            @media (max-width: 480px) {
              font-size: 14px;
              line-height: 20px;
            }
          }
        }

        .action-block {
          display: flex;
          justify-content: space-between;
          padding: 8px;

          .link-resource {
            display: flex;
            align-items: center;
            padding: 4px 14px;
            gap: 6px;
            border-radius: 5px;
            cursor: pointer;

            &:hover {
              background: #ffede2;
            }

            &:active {
              background: #ffdbc4;
            }
          }

          .link-resource,
          .function-filter .filter {
            color: #974900;
            font-size: 12px;
            font-weight: 600;
            line-height: 20px;
            letter-spacing: 0.15px;
          }

          .function-filter {
            display: flex;
            align-items: center;
            gap: 8px;
            color: #4c4541;
            font-size: 14px;
            font-weight: 600;
            line-height: 20px;

            .filter {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 4px 10px;
              border-radius: 5px;
              cursor: pointer;

              .arrow-down {
                width: 12px;
                height: 12px;
              }

              &:hover {
                background: #ffede2;
              }

              &:active {
                background: #ffdbc4;
              }
            }

            @media (max-width: 480px) {
              font-size: 12px;
              line-height: 16px;
            }
          }

          @media (max-width: 480px) {
            padding-top: 0;
          }
        }

        .resource-list {
          padding-bottom: 16px;
          &.enable-scroll {
            overflow-y: auto;
            max-height: 570px;

            @media (max-width: 480px) {
              max-height: 470px;
            }
          }

          .resource-row {
            display: flex;
            align-items: center;
            justify-content: space-between;
            border-bottom: 1px solid #eee;
            background: #fff;
            cursor: pointer;

            .user-info-block {
              display: flex;
              align-items: center;
              gap: 8px;
              padding: 7.5px 16px;
              overflow: hidden;

              .picture {
                img {
                  width: 40px;
                  border-radius: 50%;
                  @media (max-width: 480px) {
                    width: 30px;
                  }
                }
              }

              .check-resource {
                display: none;
                padding: 9px 0;

                &:has(.checked) {
                  display: flex;
                }

                .checkbox-container {
                  margin: 0;
                }
              }

              .text-block {
                overflow: hidden;
                .resource-name {
                  color: #4c4541;
                  font-size: 14px;
                  font-weight: 600;
                  line-height: 20px;
                  white-space: nowrap;
                  word-break: break-all;
                  overflow: hidden;
                  text-overflow: ellipsis;

                  @media (max-width: 480px) {
                    font-size: 12px;
                    line-height: 16px;
                  }
                }
                .resource-code {
                  color: #998f8a;
                  font-size: 12px;
                  font-weight: 400;
                  line-height: 16px;

                  @media (max-width: 480px) {
                    font-size: 10px;
                    line-height: 14px;
                  }
                }
              }
            }

            .view-block {
              display: none;
              gap: 8px;
              align-items: center;
              padding-right: 16px;
              color: #974900;
              font-size: 12px;
              font-weight: 400;
              line-height: 16px;
            }

            .roles-block {
              display: flex;
              justify-content: flex-end;
              gap: 8px;
              padding-right: 16px;
              .role-tag {
                white-space: nowrap;
                border-radius: 4px;
                background: #eceff1;
                padding-inline: 9px;
                color: #607d8b;
                text-align: center;
                font-size: 12px;
                font-weight: 600;
                line-height: 16px;
              }
            }

            .unlink {
              display: none;
              cursor: pointer;
              padding: 8px;
              margin-right: 16px;
              border-radius: 5px;

              &:hover {
                background-color: #ffede2 !important;
              }

              &:active {
                background-color: #ffdbc4 !important;
              }
            }

            &.--is-viewing {
              background: #ffede2;
              border-color: #974900;
              .user-info-block {
                .text-block {
                  .resource-code,
                  .resource-name {
                    color: #974900;
                  }
                }
              }

              .view-block {
                display: flex;
              }

              .roles-block {
                display: none;
              }

              &.is-mobile {
                border-bottom: 1px solid #eee;
                background: #fff;
                .view-block {
                  display: none;
                }

                .roles-block {
                  display: flex;
                }
              }
            }

            &:hover,
            &:active {
              &:has(.checked) {
                border-color: #eee;
                background: #fff;
                .user-info-block {
                  .picture {
                    display: none;

                    @media (max-width: 480px) {
                      display: block;
                    }
                  }
                  .check-resource {
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    @media (max-width: 480px) {
                      display: none;
                    }
                  }
                  .text-block {
                    .resource-name {
                      color: #4c4541;
                    }
                    .resource-code {
                      color: #998f8a;
                    }
                  }
                }
                .view-block {
                  display: none;
                }
                .roles-block {
                  display: flex;
                }
                .unlink {
                  display: none;
                }
              }

              &.is-mobile {
                .view-block {
                  display: none;
                }

                .roles-block {
                  display: none;
                }
              }

              background: #ffede2;
              border-color: #974900;
              .user-info-block {
                .picture {
                  display: none;

                  @media (max-width: 480px) {
                    display: block;
                  }
                }
                .check-resource {
                  display: flex;
                  align-items: center;
                  justify-content: center;

                  @media (max-width: 480px) {
                    display: none;
                  }
                }
                .text-block {
                  .resource-code,
                  .resource-name {
                    color: #974900;
                  }
                }
              }
              .view-block {
                padding-right: 8px;
              }
              .roles-block {
                display: none;
              }
              .unlink {
                display: flex;

                @media (max-width: 480px) {
                  display: none;
                }
              }
            }
          }

          .block-no-results {
            padding-block: 16px;
            .no-results {
              height: calc(100vh - 200px);
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 16px;
              border-radius: 6px;
              border: 1px solid #cfc4be;

              svg {
                width: 16px;
                height: 16px;
                fill: #998f8a;
              }

              .text {
                margin: 0;
                color: #998f8a;
                font-size: 16px;
                font-weight: 600;
                line-height: 26px;
              }

              @media (max-width: 480px) {
                svg {
                  width: 12px;
                  height: 12px;
                }
                .text {
                  font-size: 12px;
                  line-height: 16px;
                }
              }
            }
          }

          &::-webkit-scrollbar {
            width: 7px;
          }

          &::-webkit-scrollbar-track {
            background: #fff;
          }

          &::-webkit-scrollbar-thumb {
            background-color: #cfc4be;
            border-radius: 9px;
          }
        }

        .checkbox-container {
          display: flex;
          background-color: #fff;
          border: 1px solid #cfc4be;
          border-radius: 3px;
          margin-top: 4px;
          cursor: pointer;
          width: 18px;
          height: 18px;

          .icon-checkbox {
            display: flex !important;
            align-items: center;
            justify-content: center;
            margin-top: 2px;
            svg {
              width: 12px;
              height: 12px;
            }
          }
          &.checked {
            background-color: #974900;
            display: block;
          }

          @media (max-width: 480px) {
            display: none;
          }
        }

        &::-webkit-scrollbar {
          width: 7px;
        }

        &::-webkit-scrollbar-track {
          background: #fff;
        }

        &::-webkit-scrollbar-thumb {
          background-color: #cfc4be;
          border-radius: 9px;
        }
      }
    }
  }
</style>
